import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import WappIcono, { TIPOS } from "../../../components/wappIcono";
import { DlgCompartir } from "./dialogos";
import { copiarEnlace, enlaceDe, RUTA_APP } from "../../../services/v2/wappdrive.service";

const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "No se pudo abrir.";
const NOMBRE: Record<string, string> = { word: "WappDocs", cell: "WappCell", slide: "WappDeck", pdf: "WappPDF" };

/** "2:34 pm" (siempre am/pm, como en el resto de WAPP). */
export const horaAmPm = (d: Date): string => { const h = d.getHours(); return (h % 12 || 12) + ":" + String(d.getMinutes()).padStart(2, "0") + (h < 12 ? " am" : " pm"); };
const fechaHora = (s: string | null | undefined): string => {
	if (!s) return "";
	const d = new Date(s.includes("T") || s.endsWith("Z") ? s : s.replace(" ", "T") + "Z");
	if (isNaN(d.getTime())) return "";
	const hoy = new Date();
	const mismoDia = d.toDateString() === hoy.toDateString();
	return (mismoDia ? "hoy" : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" })) + " a las " + horaAmPm(d);
};

/**
 * Editor de celular (type "mobile") en pantallas angostas y en cualquier tablet o iPad sin mouse/trackpad, aunque esté
 * girada: el editor de computadora en pantalla táctil no abre el teclado al tocar una celda (caso Suamy, 2026-10-29).
 * El iPad se presenta como Mac, por eso se decide por el tipo de puntero y no por el navegador.
 */
export const editorMovil = (): boolean => {
	const mm = (q: string) => !!window.matchMedia?.(q).matches;
	return mm("(max-width: 700px)") || (mm("(pointer: coarse)") && !mm("(any-pointer: fine)"));
};

/** Carga api.js del servidor de documentos una sola vez. */
let cargando: Promise<void> | null = null;
export const cargarApi = (servidor: string): Promise<void> => {
	if ((window as any).DocsAPI) return Promise.resolve();
	if (!cargando) {
		cargando = new Promise((ok, mal) => {
			const s = document.createElement("script");
			s.src = servidor + "/web-apps/apps/api/documents/api.js";
			s.onload = () => ok();
			s.onerror = () => { cargando = null; mal(new Error("No se pudo conectar con el editor.")); };
			document.head.appendChild(s);
		});
	}
	return cargando;
};

/**
 * WappDocs / WappCell a pantalla completa (OnlyOffice). Varias personas editan el mismo archivo a la vez; se guarda solo.
 * Como Google Docs, cada archivo tiene su propia dirección (/wappcell?d=123) en la barra del navegador, que se puede
 * copiar y mandar; "Atrás" del navegador cierra el editor. Clic derecho en una celda → "Obtener vínculo" da el enlace
 * directo a esa celda o rango (&a=…).
 */
const Editor: React.FC<{ campusId: number; id: number; onCerrar: () => void }> = ({ campusId, id, onCerrar }) => {
	const caja = useRef<HTMLDivElement>(null);
	const inst = useRef<any>(null);
	const ruta = useRef<{ empujada: boolean; salida: string; titulo: string }>({ empujada: false, salida: window.location.pathname, titulo: document.title });
	const [error, setError] = useState<string | null>(null);
	const [info, setInfo] = useState<{ nombre: string; tipo: string; puedeEditar: boolean } | null>(null);
	const [compartir, setCompartir] = useState<boolean>(false);
	const [copiado, setCopiado] = useState<boolean>(false);
	// Auto-guardado con hora exacta (Esteban, 2026-10-09): "Guardando…" mientras hay cambios sin guardar y "Auto-guardado a las 2:34 pm".
	const [guardado, setGuardado] = useState<{ estado: "inicio" | "guardando" | "listo"; texto: string }>({ estado: "inicio", texto: "" });
	// Sesión vieja o error del editor (Esteban, 2026-10-15): antes se seguía escribiendo sin que nada se guardara.
	// vuelta = volver a abrir el editor con lo último de WappDrive; problema = aviso con "Recargar".
	const [vuelta, setVuelta] = useState<number>(0);
	const [problema, setProblema] = useState<string | null>(null);
	const domId = "wd-editor-" + id;
	const cerrarRef = useRef(onCerrar);
	cerrarRef.current = onCerrar;

	const cerrar = () => {
		if (ruta.current.empujada) { window.history.back(); return; } // el popstate cierra
		window.history.replaceState(null, "", ruta.current.salida);
		cerrarRef.current();
	};

	useEffect(() => {
		let vivo = true;
		const movil = editorMovil();
		const accion = new URLSearchParams(window.location.search).get("a");
		HttpManager.getInstance().wappdriveService.editor(campusId, id, !!movil, accion || undefined).then(async (r) => {
			await cargarApi(r.data.servidor);
			if (!vivo) return;
			setInfo({ nombre: r.data.nombre, tipo: r.data.tipo, puedeEditar: r.data.puedeEditar });
			setGuardado({ estado: "inicio", texto: (r.data as any).modificado ? "Última modificación: " + fechaHora((r.data as any).modificado) : "" });
			document.title = r.data.nombre.replace(/\.(docx|xlsx|pptx|pdf)$/i, "") + " - " + (NOMBRE[r.data.tipo] || "WappCloud");
			const app = RUTA_APP[r.data.tipo];
			if (app && vuelta === 0) {   // al recargar el editor la dirección ya está puesta (no se empuja otra vez)
				const propia = "/" + app + "?d=" + id;
				const ahora = window.location.pathname + window.location.search;
				if (!ahora.startsWith(propia)) {
					ruta.current = { ...ruta.current, empujada: true, salida: ahora };
					window.history.pushState({ wdEditor: id }, "", propia);
				} else {
					ruta.current = { ...ruta.current, empujada: false, salida: "/" + app };
				}
			}
			inst.current = new (window as any).DocsAPI.DocEditor(domId, {
				...r.data.config,
				events: {
					onRequestClose: cerrar,
					// true = hay cambios que todavía no llegan al servidor; false = ya quedaron guardados.
					onDocumentStateChange: (e: any) => setGuardado(e.data ? { estado: "guardando", texto: "Guardando…" } : { estado: "listo", texto: "Auto-guardado a las " + horaAmPm(new Date()) }),
					// "Obtener vínculo" de una celda/rango/marcador: el editor nos da los datos y le regresamos el enlace.
					onMakeActionLink: (e: any) => inst.current?.setActionLink(enlaceDe({ id, editor: r.data.tipo }) + "&a=" + encodeURIComponent(JSON.stringify(e.data))),
					// El archivo cambió de versión mientras estaba abierto: se vuelve a abrir solo con lo último (lo pide el editor).
					onRequestRefreshFile: () => { setProblema(null); setVuelta((v) => v + 1); },
					onOutdatedVersion: () => { setProblema(null); setVuelta((v) => v + 1); },
					// Error del editor (sin conexión, no pudo guardar…): aviso visible con Recargar en vez de seguir escribiendo a ciegas.
					onError: (e: any) => setProblema("El editor tuvo un problema" + (e?.data?.errorDescription ? ": " + e.data.errorDescription : "") + ". Recarga para seguir sin perder cambios."),
				},
			});
		}).catch((e) => vivo && setError(errorTexto(e)));
		const atras = () => { if (ruta.current.empujada) { ruta.current.empujada = false; cerrarRef.current(); } };
		window.addEventListener("popstate", atras);
		return () => {
			vivo = false;
			window.removeEventListener("popstate", atras);
			document.title = ruta.current.titulo;
			try { inst.current?.destroyEditor(); } catch (e) { /* ya cerrado */ }
		};
	}, [campusId, id, vuelta]);

	const copiar = async () => { await copiarEnlace({ id, editor: info?.tipo }); setCopiado(true); setTimeout(() => setCopiado(false), 2500); };

	return (
		<div className="wd-editor" role="dialog" aria-label="Editor">
			<div className="wd-editor-cab">
				<button className="wd-editor-x" onClick={cerrar} aria-label="Volver" title="Volver"><WappIcono n="back" /></button>
				{info ? <span className="wd-tipo" style={{ background: TIPOS[info.tipo]?.fondo, color: TIPOS[info.tipo]?.tinta }}><WappIcono n={TIPOS[info.tipo]?.icono || "file"} /></span> : null}
				<div className="wd-editor-tit">
					<b>{info ? info.nombre.replace(/\.(docx|xlsx|pptx|pdf)$/i, "") : "Abriendo…"}</b>
					<span className={"wd-guardado " + guardado.estado}>
						{info ? (NOMBRE[info.tipo] || "Documento") + " · " + (!info.puedeEditar ? "solo lectura" : guardado.texto || "Auto-guardado") : ""}
						{info?.puedeEditar && guardado.estado === "listo" ? <WappIcono n="check" /> : null}
					</span>
				</div>
				<span style={{ flex: 1 }} />
				{/* Crédito y código del editor (AGPL v3) sin el nombre a la vista (Esteban, 2026-11-01: "esconde OnlyOffice lo más posible"). */}
				<a className="wd-credito" href="https://github.com/iawashingtonacademy/onlyoffice-docs-wa" target="_blank" rel="noopener noreferrer" title="Acerca del editor: basado en ONLYOFFICE Docs (AGPL v3), código y cambios" aria-label="Acerca del editor"><WappIcono n="info" /></a>
				{info ? <button className={"wd-editor-enlace" + (copiado ? " ok" : "")} onClick={copiar} title="Copiar enlace" aria-label="Copiar enlace"><WappIcono n={copiado ? "check" : "link"} /><span>{copiado ? "Copiado" : ""}</span></button> : null}
				{info ? <button className="wd-btn wd-editor-compartir" onClick={() => setCompartir(true)}><WappIcono n="share" /> <span>Compartir</span></button> : null}
			</div>
			{compartir && info ? <DlgCompartir campusId={campusId} nodo={{ id, name: info.nombre, editor: info.tipo } as any} onClose={() => setCompartir(false)} /> : null}
			{error ? <div className="wd-aviso">{error}</div> : null}
			{problema ? <div className="wd-aviso" role="alert" onClick={() => { setProblema(null); setVuelta((v) => v + 1); }}>{problema} <b>Recargar</b></div> : null}
			<div className="wd-editor-cuerpo" ref={caja}><div id={domId} /></div>
		</div>
	);
};

export default Editor;
